import React, { useState } from "react";
import { Upload, FileUp } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import AdminLayout from "./AdminLayout";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

// Accepts CSV lines "dd-mm-yyyy,FR,SR" (or dd/mm/yyyy) OR a JSON array
// [{ "date":"dd-mm-yyyy", "fr":"12", "sr":"34" }].
function parseInput(text) {
  const trimmed = text.trim();
  if (!trimmed) return [];
  // JSON
  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    const arr = JSON.parse(trimmed);
    const list = Array.isArray(arr) ? arr : [arr];
    return list.map((o) => ({
      date: String(o.date || "").replace(/\//g, "-").trim(),
      fr: String(o.fr ?? "").trim(),
      sr: String(o.sr ?? "").trim(),
    }));
  }
  // CSV
  return trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !/^date/i.test(line))
    .map((line) => {
      const parts = line.split(/[,\t;]/).map((p) => p.trim());
      return {
        date: (parts[0] || "").replace(/\//g, "-"),
        fr: parts[1] || "",
        sr: parts[2] || "",
      };
    });
}

const BulkImport = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setText(await f.text());
  };

  const doImport = async () => {
    let items;
    try {
      items = parseInput(text);
    } catch (err) {
      toast({ title: "Could not parse input. Check CSV/JSON format.", variant: "destructive" });
      return;
    }
    const valid = items.filter((i) => /^\d{2}-\d{2}-\d{4}$/.test(i.date) && i.fr);
    if (valid.length === 0) {
      toast({ title: "No valid rows found (need date dd-mm-yyyy and F/R).", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const res = await api.bulkImportResults(valid);
      setResult(res);
      queryClient.invalidateQueries({ queryKey: ["results"] });
      queryClient.invalidateQueries({ queryKey: ["analytics"] });
      queryClient.invalidateQueries({ queryKey: ["predict"] });
      toast({ title: `Imported ${res.inserted}, skipped ${res.skipped} (existing)` });
    } catch (err) {
      toast({ title: "Import failed", variant: "destructive" });
    }
    setBusy(false);
  };

  const field =
    "w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#4a7fb5]";

  return (
    <AdminLayout title="Bulk Import" showBack>
      <div className="bg-white border-2 border-dashed border-[#3aa79a] rounded-lg p-5 space-y-4 max-w-2xl">
        <div className="flex items-center gap-2 text-[#1f2a37]">
          <Upload className="w-5 h-5" />
          <h2 className="text-lg font-bold">Import Verified Historical Results</h2>
        </div>
        <p className="text-sm text-[#5b6b7b]">
          Paste <b>verified</b> results as CSV (one per line:
          <code className="mx-1 bg-gray-100 px-1 rounded">dd-mm-yyyy,FR,SR</code>)
          or a JSON array. Existing dates are <b>never overwritten</b> — only
          missing dates are added.
        </p>

        <label className="inline-flex items-center gap-2 text-sm text-[#2f5d84] cursor-pointer hover:underline">
          <FileUp className="w-4 h-4" />
          Upload a .csv / .json file
          <input type="file" accept=".csv,.json,.txt" onChange={onFile} className="hidden" />
        </label>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          placeholder={"06-08-2026,87,56\n05-08-2026,30,43\n...\n\nor JSON:\n[{\"date\":\"06-08-2026\",\"fr\":\"87\",\"sr\":\"56\"}]"}
          className={field}
        />

        <button
          onClick={doImport}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-[#4a7fb5] hover:bg-[#3d6c9c] disabled:opacity-60 text-white font-medium px-6 py-2.5 rounded-md transition-colors"
        >
          <Upload className="w-4 h-4" /> {busy ? "Importing..." : "Import"}
        </button>

        {result && (
          <div className="text-sm bg-[#f1f9f7] border border-[#bfe3db] rounded-md p-3 text-[#1f2a37]">
            Inserted: <b>{result.inserted}</b> · Skipped (already existed):{" "}
            <b>{result.skipped}</b> · Total in database: <b>{result.total}</b>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default BulkImport;
